import type { ButtonProps } from '@/components/ui/button'
import { IconButton } from '@/components/ui/icon-button'
import * as Menu from '@/components/ui/menu'
import { A } from '@solidjs/router'
import { ArrowRight, AtSign, Bell, BellOff, EllipsisVertical } from 'lucide-solid'
import { type Component, type JSX, Show } from 'solid-js'
import { Portal } from 'solid-js/web'
import { css } from 'styled-system/css'
import { Box, Circle, Flex } from 'styled-system/jsx'
import { UnreadCount } from './UnreadCount'

// --- SHARED LIST CONTAINER ---
export const SharedChannelList: Component<{
  children: JSX.Element
}> = (props) => {
  // Removed the old channelReadsRefetch effect.
  // The layout now simply wraps the list.
  return (
    <Flex
      overflow="auto"
      alignItems="stretch"
      gap={1}
      w='full'
      direction="column"
      class="scrollbar"
    >
      {props.children}
    </Flex>
  )
}

export type SharedChannelItemProps = {
  href: string
  end?: boolean
  isMinimal?: boolean
  minimalVariant?: ButtonProps['variant']
  channelId: string
  isSubscribed?: boolean
  onToggleSubscribe: (id: string) => void
  linkOutHref?: string
  buttonProps?: ButtonProps
  unreadCount?: number // <-- Now strictly a number from our RPC
  hasMention?: boolean
  children: JSX.Element
}

export const SharedChannelItem: Component<SharedChannelItemProps> = (props) => {
  return (
    <Box
      class={css({
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        borderRadius: 'lg',
        _hover: {
          bg: 'editorialHover',
        },
        '&:has(.active-channel)': {
          bg: 'jade.4',
          _hover: {
            bg: 'jade.4',
          },
        },
      })}
    >
      <A
        href={props.href}
        end={props.end}
        activeClass="active-channel"
        class={css({
          display: 'flex',
          flex: 1,
          minW: 0,
          alignItems: 'center',
          py: 1,
          px: 2,
          gap: 1,
          minHeight: '10',
          _before: {
            content: '""',
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            borderRadius: 'lg',
          },
        })}
      >
        {/* Left side: Consumer's custom content */}
        <Box position="relative" zIndex={1} flex="1" minW={0}>
          <Flex flex="1" minW={0} alignItems="center" gap={2} overflow="hidden">
            {props.children}
          </Flex>
        </Box>

        {/* Right side: Shared indicators */}
        <Flex alignItems="center" gap={1} shrink={0} position="relative" zIndex={1}>
          <Show when={props.hasMention}>
            <Circle
              class={css({
                background: 'bg.subtle',
                h: 5,
                w: 5,
              })}
            >
              <AtSign size={12} />
            </Circle>
          </Show>

          {/* Evaluate > 0 so we don't render a badge for 0 */}
          <Show when={props.isSubscribed}>
            <Circle
              class={css({
                background: 'bg.subtle',
                h: 5,
                w: 5,
              })}
            >
              <Bell size={12} />
            </Circle>
          </Show>

          <Show when={props.unreadCount && props.unreadCount > 0}>
            <UnreadCount count={props.unreadCount!} />
          </Show>
        </Flex>
      </A>

      {/* Menu Actions (Sibling to the A tag) */}
      <Show when={!props.isMinimal}>
        <Box position="relative" zIndex={999999} pr={2}>
          <Menu.Root positioning={{ placement: 'left' }}>
            <Menu.Trigger
              asChild={(triggerProps) => (
                <IconButton
                  {...triggerProps}
                  size="xs"
                  variant="plain"
                  colorPalette={props.isSubscribed ? 'jade' : 'fg'}
                >
                  <EllipsisVertical />
                </IconButton>
              )}
            />

            {/* <Portal> */}
            <Menu.Positioner>
              <Menu.Content>
                <Menu.Item
                  value="toggle-subscribe"
                  onClick={() => props.onToggleSubscribe(props.channelId)}
                >
                  <Show
                    when={props.isSubscribed}
                    fallback={
                      <>
                        <Bell size={16} /> Subscribe
                      </>
                    }
                  >
                    <>
                      <BellOff size={16} /> Unsubscribe
                    </>
                  </Show>
                </Menu.Item>

                <Show when={props.linkOutHref}>
                  <A href={props.linkOutHref!} class={css({ display: 'contents' })}>
                    <Menu.Item value="view-setup">
                      <ArrowRight size={16} /> View Setup
                    </Menu.Item>
                  </A>
                </Show>
              </Menu.Content>
            </Menu.Positioner>
            {/* </Portal> */}
          </Menu.Root>
        </Box>
      </Show>
    </Box>
  )
}
